import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame } from '../components/Frame';
import { KrabenMark } from '../components/KrabenMark';
import { Avatar } from '../components/Avatar';
import { CREW, type CrewId } from '../data/crew';
import { RESET, getChallenge } from '../data/exercises';
import { loadProfile, saveProfile } from '../lib/state';
import { listMyPosts, type FeedPost } from '../lib/feed';
import { useEffect } from 'react';
import { track } from '../analytics';
import { comebackGift } from '../lib/crew';
import { useViewed } from '../lib/viewed';

/**
 * The welcome-back pair (06-month-one-moments.md R-08, R-09): the most
 * celebrated moment in the app. Decorated, the crew waving, filled dots only,
 * no gap shown, no question about where you went. Then: coming back is the
 * hard part, that one counts; the shortest exercise; one gifted drawing.
 */
export function WelcomeBack() {
  const nav = useNavigate();
  const [step, setStep] = useState<0 | 1>(0);
  useViewed('welcome_back', `step_${step}`);
  const profile = loadProfile();
  const shortest = RESET[profile.path].map((id) => getChallenge(id)!).sort((a, b) => a.minutes - b.minutes)[0];
  const [last, setLast] = useState<FeedPost | null>(null);
  useEffect(() => { listMyPosts(1).then((p) => setLast(p[0] || null)).catch(() => {}); track('welcome_back_shown', { madeDays: profile.madeDays }); }, [profile.madeDays]);

  const seen = () => {
    const p = { ...loadProfile(), welcomedOn: new Date().toISOString().slice(0, 10) };
    saveProfile(p);
    comebackGift().catch(() => {});
  };

  if (step === 0) {
    return (
      <Frame left={<span className="tag">Day {profile.madeDays}</span>}>
        <div className="bunting" aria-hidden="true">{Array.from({ length: 9 }, (_, i) => <span key={i} className={`flag f${i % 4}`} />)}</div>
        <h1 style={{ textAlign: 'center' }}>Welcome back{profile.name ? `, ${profile.name}` : ''}.</h1>
        <div className="crew-row" style={{ justifyContent: 'center' }}>
          <div className="crew-face" style={{ marginTop: -6 }}><KrabenMark size={92} /></div>
          {(['mrs-jennys', 'the-moon', 'the-censor'] as CrewId[]).map((id) => <div key={id} className="crew-face"><Avatar who={id} size={60} /><span className="hint">{CREW[id].name}</span></div>)}
        </div>
        {last && <img src={last.cardUrl} alt={last.headline} className="pinned" />}
        <div className="stack" style={{ alignItems: 'center', gap: 6 }}>
          <span className="counter">Day {profile.madeDays} of making things</span>
          <div className="dots">{Array.from({ length: profile.madeDays }, (_, i) => <span key={i} className="dot" />)}</div>
        </div>
        <div className="stack" style={{ marginTop: 'auto' }}>
          <button className="btn" onClick={() => setStep(1)}>Come in</button>
        </div>
      </Frame>
    );
  }

  return (
    <Frame left={<span className="tag">Day {profile.madeDays}</span>}>
      <div className="row between"><h1>Coming back is the hard part. That one counts.</h1><KrabenMark size={72} /></div>
      <div className="sheet" style={{ gap: 6 }}>
        <span className="label">On the house</span>
        <b>One drawing by your crew, whenever you want it.</b>
        <span className="hint">Kept for you. It never runs out.</span>
      </div>
      <div className="sheet">
        <span className="label">Today · {shortest.minutes} min</span>
        <h2>{shortest.title}</h2>
        {shortest.encouragement && <p className="why">{shortest.encouragement}</p>}
        <button className="btn" onClick={() => { seen(); track('comeback_started', { challengeId: shortest.id }); nav(`/do/${shortest.id}`, { state: { door: 'front' } }); }}>Start</button>
      </div>
      <p className="hint" style={{ textAlign: 'center' }}>No backlog. Yesterday’s exercise is not waiting.</p>
      <div className="stack" style={{ marginTop: 'auto' }}>
        <button className="btn ghost" onClick={() => { seen(); nav('/feed'); }}>Just show me my feed</button>
      </div>
    </Frame>
  );
}
